import React, { useState, useEffect, Fragment } from 'react';
import { RouteComponentProps, navigate } from '@reach/router';
import Input from 'components/Form/Input';
import * as userTypes from 'state/user/UserTypes';
import { RFC } from 'state/StateTypes';
import { InputChange, FormSubmission, ButtonClick, Error, ErrorCodes } from '../types';
import {
    updateProfile,
    getProfile,
    requestVerification,
    changePassword,
} from 'state/user/UserActions';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'components/Button';
import { AppState } from 'state/store';
import styled from 'styled-components';
import isAuthenticated from 'hooks/isAuthenticated';
import Loading from 'components/Loading';
import ErrorMessage from 'components/ErrorMessage';
import SecondaryButton from 'components/SecondaryButton';
import Container from 'components/Container';
import FormContainer from 'components/FormContainer';
import Legend from 'components/Legend';
import Form from 'components/Form';
import Divider from 'components/Divider';
import Instructions from 'components/Instructions';
import FormHeader from 'components/FormHeader';
import { selectCurrentUser } from 'state/user/UserSelectors';
import BackButton from 'components/BackButton';
import { validateEmail, presentErrorBasedOnCode } from 'utils';

const initialFormState: userTypes.UpdateProfileRequest = {
    email: '', //username
    firstName: '',
    lastName: '',
    phone: '',
};

const EditAccountInfo: RFC<RouteComponentProps> = () => {
    const authenticated: boolean = isAuthenticated();
    const [form, setForm] = useState<userTypes.UpdateProfileRequest>(initialFormState);
    const dispatch = useDispatch();
    const user: userTypes.User | null = useSelector((state: AppState) => selectCurrentUser(state));
    const [isChangingPassword, setIsChangingPassword] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [verificationSent, setVerificationSent] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [prevPassword, setPrevPassword] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    const [error, setError] = useState<Error | null>(null);
    const [responseMessage, setResponseMessage] = useState<string>('');
    const [formComplete, setFormComplete] = useState<boolean>(false);
    const [isValidEmail, setIsValidEmail] = useState<boolean>(false);

    useEffect(() => {
        (async () => {
            if (authenticated) {
                try {
                    await dispatch(getProfile());
                } catch (error) {
                    //if error results from missing token it will not have a json method
                    error = error.json ? await error.json() : error;
                    setError(error);
                }
                setLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        if (user) {
            //pre-fill form with user info
            const { email, firstName, lastName, phone } = user;
            setForm({ email, firstName, lastName, phone });
        }
    }, [user]);

    useEffect(() => {
        if (!!form.email && !!form.firstName && !!form.phone) {
            setIsValidEmail(validateEmail(form.email));
            isValidEmail && setFormComplete(true);
        } else {
            setFormComplete(false);
        }
    }, [form.email, form.firstName, form.phone, isValidEmail]);

    const handleInput = (e: InputChange): void => {
        e.persist();
        setForm(form => ({ ...form, [e.target.name]: e.target.value }));
    };

    const handleVerificationRequest = async (e: any) => {
        e.preventDefault();
        try {
            const message: string = await requestVerification({ attribute: 'email' });
            setResponseMessage(message);
            setVerificationSent(true);
        } catch (error) {
            //if error results from missing token it will not have a json method
            error = error.json ? await error.json() : error;
            setError(error);
        }
    };

    const handleSubmit = async (e: FormSubmission): Promise<void> => {
        e.preventDefault();
        try {
            setSubmitting(true);
            await dispatch(updateProfile(form));
            await navigate('/profile');
        } catch (error) {
            //if error results from missing token it will not have a json method
            error = error.json ? await error.json() : error;
            setError(error);
        }
        setSubmitting(false);
    };

    const handleChangePassword = async (e: ButtonClick): Promise<void> => {
        e.preventDefault();
        try {
            const message: string = await changePassword({ password: prevPassword, newPassword });
            setResponseMessage(message);
        } catch (error) {
            //if error results from missing token it will not have a json method
            error = error.json ? await error.json() : error;
            setError(error);
        }
        setIsChangingPassword(false);
    };
    return loading ? (
        <Loading />
    ) : (
        <Container margin="0 auto">
            <BackButton to="/profile" />
            <FormContainer>
                <Header>EDIT ACCOUNT INFO</Header>
                <Legend>
                    Fields with <span>*</span> are required
                </Legend>
                <Form onSubmit={handleSubmit}>
                    <Input
                        label="Email"
                        value={form.email}
                        name={userTypes.UserKeys.email}
                        onChange={handleInput}
                        size="full"
                        errorMessage="Email is required"
                        type="email"
                        required={true}
                    />
                    <Input
                        label="First Name"
                        value={form.firstName}
                        name={userTypes.UserKeys.firstName}
                        onChange={handleInput}
                        size="full"
                        type="text"
                        required={true}
                    />
                    <Input
                        label="Last Name"
                        value={form.lastName}
                        name={userTypes.UserKeys.lastName}
                        onChange={handleInput}
                        size="full"
                        type="text"
                    />
                    <Input
                        label="Phone"
                        value={form.phone}
                        name={userTypes.UserKeys.phone}
                        onChange={handleInput}
                        size="full"
                        type="text"
                        required={true}
                    />
                    {/* todo: determine requirements for disabling */}
                    <Button isLoading={submitting} disabled={!formComplete}>
                        Save
                    </Button>
                </Form>
                {user && !user.verified && (
                    <Fragment>
                        <Divider />
                        <EmailVerificationSection>
                            <Header>Email Verification</Header>
                            <Instructions>Email is not verified</Instructions>
                            {!verificationSent && (
                                <SecondaryButton color="green" onClick={handleVerificationRequest}>
                                    Request Verification
                                </SecondaryButton>
                            )}
                            {verificationSent && (
                                <Instructions>
                                    A verification code has been sent to{' '}
                                    <UserEmail>{user && user.email}</UserEmail>!
                                </Instructions>
                            )}
                        </EmailVerificationSection>
                    </Fragment>
                )}
                <ChangePassword
                    isChangingPassword={isChangingPassword}
                    onClick={() => setIsChangingPassword(!isChangingPassword)}>
                    {!isChangingPassword ? 'Change Password' : 'Cancel'}
                </ChangePassword>
                {error && (
                    <ErrorMessage>{presentErrorBasedOnCode(error.code as ErrorCodes)}</ErrorMessage>
                )}
            </FormContainer>
            <ChangePasswordSection>
                {isChangingPassword && (
                    <FormContainer>
                        <Form>
                            <Input
                                value={prevPassword}
                                name={userTypes.UserKeys.password}
                                label="Current password"
                                onChange={(e: InputChange) => setPrevPassword(e.target.value)}
                                size="full"
                                type="password"
                            />
                            <Input
                                value={newPassword}
                                name={userTypes.UserKeys.password}
                                label="New password"
                                onChange={(e: InputChange) => setNewPassword(e.target.value)}
                                size="full"
                                type="password"
                            />
                            <SecondaryButton
                                color="blue"
                                disabled={!prevPassword || newPassword.length < 8}
                                onClick={handleChangePassword}>
                                Change Password
                            </SecondaryButton>
                        </Form>
                        {error && (
                            <ErrorMessage>
                                {presentErrorBasedOnCode(error.code as ErrorCodes)}
                            </ErrorMessage>
                        )}
                    </FormContainer>
                )}
            </ChangePasswordSection>
        </Container>
    );
};

export default EditAccountInfo;

const Header = styled(FormHeader)`
    margin: 0 auto 0.5em auto;
`;

export const ChangePassword = styled.button.attrs({ type: 'button' })`
    color: ${({ theme, isChangingPassword }: { theme: any; isChangingPassword: boolean }) =>
        isChangingPassword ? theme.secondaryRed : theme.secondaryBlueDark};
    padding: 5px 7px;
    border-radius: 5px;
    border: 1px solid
        ${({ theme, isChangingPassword }: { theme: any; isChangingPassword: boolean }) =>
            isChangingPassword ? theme.secondaryRed : theme.secondaryBlueDark};
    background-color: transparent;
    font-size: 1em;
    font-weight: 700;
    align-self: center;
    cursor: pointer;
    margin-top: 2em;
    &:hover {
        color: ${({ theme }) => theme.secondaryWhite};
        background-color: ${({
            theme,
            isChangingPassword,
        }: {
            theme: any;
            isChangingPassword: boolean;
        }) => (isChangingPassword ? theme.secondaryRed : theme.secondaryBlueLight)};
        transition-duration: 200ms;
    }
`;

const EmailVerificationSection = styled.div`
    padding: 5px 10px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    width: 100%;
    margin: 0 auto;

    > * {
        margin: 5px auto;
    }
`;

const ChangePasswordSection = styled.div`
    text-align: center;
`;

const UserEmail = styled.span`
    color: ${({ theme }) => theme.secondaryBlueDark};
    font-weight: thin;
`;
